import { arrowLeft, iconPlus, iconRemove } from 'assets';
import { Modal } from 'components';
import Button from 'components/Button';
import ButtonSubmit from 'components/Forms/ButtonSubmit';
import SelectField from 'components/Forms/SelectField';
import { STATUS_MODAL } from 'constant';
import { ModalAddEdit } from 'containers/GranttChart';
import { FieldArray, FormikProvider, useFormik } from 'formik';
import { DatePicker, Form } from 'formik-antd';
import { dayjs, yup } from 'libs';
import { toNumber, uniqBy } from 'lodash';
import moment, { Moment } from 'moment';
import { FC, useCallback, useMemo, useState } from 'react';
import { PostUserSchedulesUpdateParams } from 'types';
import { useSelector } from 'react-redux';
import { workerSelector } from '../selectors';
import SectionStyled, { HeaderStyle } from './styles';
const { RangePicker } = DatePicker;
const dateFormat = 'YYYY-MM-DD';

interface Props {
  visible: boolean;
  onCancel: (cancel: boolean) => void;
  onOk: (data: PostUserSchedulesUpdateParams) => void;
  type?: Number;
  dataForm?: ModalAddEdit;
  currentDate?: Date | string;
}

type ValueUpdate = {
  work_type_id: string | null;
  building_id: string | null;
  date: Moment[][] | [][];
};

const ModalAddHistoryWork: FC<Props> = ({ dataForm, visible, onCancel, type, onOk }) => {
  const [worktype, setWorkType] = useState<string | number | null>(null);
  const [buildingID, setBuildingID] = useState<string | number | null>(null);
  const { cooperatorSchedules, userSchedules } = useSelector(workerSelector);
  const user_id = dataForm?.dataAdd?.user_id || dataForm?.dataEdit?.[0]?.user_id;

  const getDateFormSelect = useCallback(
    ({ workTypeId, buildingId, userId }) => {
      const newData = uniqBy(
        userSchedules
          .find((i) => i.user_id == userId)
          ?.sites.find((i) => i.building_id == buildingId && workTypeId == i.work_type_id)
          ?.schedules,
        (item) => item.to_date && item.from_date
      ).map((i) => {
        return [moment(i.from_date, dateFormat), moment(i.to_date, dateFormat)];
      }) ?? [[]];
      return newData.length < 1 ? [[]] : newData;
    },
    [userSchedules]
  );

  const dataInitialValues = useMemo(() => {
    let date: moment.Moment[][] | [][] = [[]];
    const work_type_id =
      worktype || dataForm?.dataAdd?.work_type_id || dataForm?.dataEdit?.[0].work_type_id;
    const building_id =
      buildingID || dataForm?.dataAdd?.building_id || dataForm?.dataEdit?.[0]?.building_id;
    if (!dataForm?.addNew) {
      date = getDateFormSelect({
        workTypeId: work_type_id,
        buildingId: building_id,
        userId: user_id,
      });
    }
    return {
      user_id,
      date,
      building_id,
      work_type_id,
    };
  }, [
    buildingID,
    dataForm?.addNew,
    dataForm?.dataAdd?.building_id,
    dataForm?.dataAdd?.work_type_id,
    dataForm?.dataEdit,
    getDateFormSelect,
    user_id,
    worktype,
  ]);
  const formik = useFormik<ValueUpdate>({
    initialValues: {
      work_type_id: dataInitialValues.work_type_id
        ? dataInitialValues.work_type_id.toString()
        : null,
      building_id: dataInitialValues.building_id ? dataInitialValues.building_id.toString() : null,
      date: dataInitialValues.date,
    },
    validationSchema,
    async onSubmit(values) {
      formik.unregisterField('date');
      if (!formik.dirty) {
        onCancelButton();
        return;
      }
      let out_of_schedule: 0 | 1 = 0;
      const newDate = values.date.map((arrDate: moment.Moment[]) => {
        return {
          from_date: moment(arrDate?.[0]).format(dateFormat),
          to_date: moment(arrDate?.[1]).format(dateFormat),
        };
      });
      const cooperatorSelect = dataForm?.allData?.find(
        (i) =>
          i.building_id == values.building_id &&
          i.work_type_id == values.work_type_id &&
          i.isScheduleCooperator
      );
      let dateMax = '';
      let dateMin = '';
      if (cooperatorSelect) {
        dateMin = cooperatorSelect?.start_date?.toString() ?? '';
        dateMax = cooperatorSelect?.end_date?.toString() ?? '';

        newDate &&
          newDate.length > 0 &&
          newDate.map((item) => {
            if (
              !dayjs(dayjs(item.from_date).toDate()).isBetween(
                dayjs(dateMin).toDate(),
                dayjs(dateMax).toDate(),
                'day',
                '[]'
              ) ||
              !dayjs(dayjs(item.to_date).toDate()).isBetween(
                dayjs(dateMin).toDate(),
                dayjs(dateMax).toDate(),
                'day',
                '[]'
              )
            ) {
              out_of_schedule = 1;
            }
          });
      }

      await onOk({
        building_id: values?.building_id ?? '',
        work_type_id: values?.work_type_id ?? '',
        user_id: user_id ?? '',
        out_of_schedule,
        date: newDate,
      });
      onCancelButton();
    },
  });

  const onCancelButton = () => {
    onCancel(false);
    setBuildingID(null);
    setWorkType(null);
    formik.resetForm();
  };
  const Header: FC = () => {
    return (
      <HeaderStyle onClick={onCancelButton}>
        <img src={arrowLeft} alt="" />
        <div>{type === STATUS_MODAL.ADD ? '作業予定の追加' : '作業予定の編集'}</div>
      </HeaderStyle>
    );
  };
  return (
    <>
      <Modal title={<Header />} visible={visible} footer={false} destroyOnClose={false} width={560}>
        <SectionStyled>
          <FormikProvider value={formik}>
            <Form layout="vertical" className="form">
              <div className="group-input">
                <div className="box-select box-top">
                  <Form.Item
                    className="input-onsite "
                    label="現場"
                    colon={false}
                    name="building_id"
                  >
                    <SelectField
                      data={cooperatorSchedules
                        .filter((s) => s.users.find((i) => i.user_id == user_id))
                        .map((item) => ({
                          value: item.building_id,
                          label: item.site_name,
                          building_code: item.building_code,
                        }))}
                      value={formik.values.building_id}
                      onSelect={async (e) => {
                        await formik.setFieldValue('building_id', e);
                        await setBuildingID(e);
                        const date = getDateFormSelect({
                          workTypeId: formik.values.work_type_id,
                          buildingId: e,
                          userId: user_id,
                        });
                        formik.setFieldValue('date', date);
                        formik.setFieldValue('work_type_id', null);
                      }}
                      name="building_id"
                      className="select-field"
                      placeholder="現場を選択してください"
                    />
                  </Form.Item>
                </div>
                <div className="box-select box-top">
                  <Form.Item
                    className="input-onsite"
                    label="工種"
                    colon={false}
                    name="work_type_id"
                  >
                    <SelectField
                      disabled={!formik.values.building_id}
                      value={formik.values.work_type_id}
                      onSelect={async (e) => {
                        await setWorkType(e);
                        await formik.setFieldValue('work_type_id', e);
                        const date = getDateFormSelect({
                          workTypeId: e,
                          buildingId: formik.values.building_id,
                          userId: user_id,
                        });
                        formik.setFieldValue('date', date);
                      }}
                      data={
                        (cooperatorSchedules.length > 0 &&
                          cooperatorSchedules
                            .find(
                              (item) => item?.building_id == toNumber(formik.values?.building_id)
                            )
                            ?.schedules.map((item) => ({
                              label: item.work_type_name,
                              value: item.work_type_id,
                            }))) ||
                        []
                      }
                      name="work_type_id"
                      className="select-field"
                      placeholder="工種を選択してください"
                    />
                  </Form.Item>
                </div>
                <div className="box_date">
                  <FieldArray
                    name="date"
                    render={(arrayHelpers) =>
                      formik.values.date.length > 0 &&
                      formik.values.date.map((_item, key) => {
                        return (
                          <div className="d-flex pick_from_to_date" key={key}>
                            <Form.Item
                              className="input-onsite"
                              label={key === 0 ? '作業日' : <div />}
                              colon={false}
                              name={`date${key}`}
                            >
                              <RangePicker
                                disabledDate={(current: Moment) => {
                                  let disable = false;
                                  if (
                                    current &&
                                    formik.values.date &&
                                    formik.values.date.length > 0
                                  ) {
                                    formik.values.date.map((item, index) => {
                                      const nowDate = moment(current).startOf('day').unix();
                                      const date1 = moment(item?.[0]).startOf('day').unix();
                                      const date2 = moment(item?.[1]).startOf('day').unix();

                                      if (date1 <= nowDate && nowDate <= date2 && index !== key) {
                                        disable = true;
                                      }
                                    });
                                  }
                                  return disable;
                                }}
                                name={`date.${key}`}
                                format={dateFormat}
                                // allowEmpty={[true, true]}
                              />
                            </Form.Item>
                            <div>
                              {formik.values.date.length > 0 &&
                              key === formik.values.date.length - 1 ? (
                                <div className="d-flex">
                                  <div className="button-add cursor-poiter">
                                    <img
                                      onClick={() => {
                                        arrayHelpers.push([]);
                                      }}
                                      src={iconPlus}
                                      alt={'iconPlus'}
                                    />
                                  </div>
                                  {formik.values.date.length !== 1 && (
                                    <div className="button-add cursor-poiter">
                                      <img
                                        onClick={() => arrayHelpers.remove(key)}
                                        src={iconRemove}
                                        alt={'iconRemove'}
                                      />
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="button-add cursor-poiter">
                                  <img
                                    onClick={() => arrayHelpers.remove(key)}
                                    src={iconRemove}
                                    alt={'iconRemove'}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    }
                  />
                </div>
              </div>
              <div className="ant-modal-footer">
                <Button
                  className="button-secondary"
                  disabled={formik.isSubmitting}
                  onClick={onCancelButton}
                >
                  キャンセル
                </Button>
                <ButtonSubmit
                  disabled={!formik.values.building_id || !formik.isValid || !formik.dirty}
                >
                  追加
                </ButtonSubmit>
              </div>
            </Form>
          </FormikProvider>
        </SectionStyled>
      </Modal>
    </>
  );
};

export default ModalAddHistoryWork;

const validationSchema = yup.object().shape({
  building_id: yup.string().nullable().required('現場を選択してください。'),
  work_type_id: yup.string().nullable().required('工種を選択してください。'),
  date: yup
    .array()
    .of(
      yup
        .array()
        .of(yup.date().required('作業日を選択してください。'))
        .min(1, '作業日を選択してください。')
    )
    .nullable()
    .required('作業日を選択してください。'),
});
